import React from 'react';
import PropTypes from 'prop-types';

import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoModules from 'mojito/modules';

import {loadScript} from '#core/utils/utils.js';
import {merge} from '#core/utils/config-utils.js';
import {Logger} from '#core/utils/logger.js';

import {BaseHeadMetaFactory} from '#core/application/seo/head-meta-factory.js';

const UIPage = MojitoCore.Presentation.UIPage;
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const PageSplitLayout = MojitoPresentation.Components.PageSplitLayout;
const FlexPane = MojitoPresentation.Components.FlexPane;
const GlobalLinks = MojitoPresentation.Components.GlobalLinks;
const DocumentHead = MojitoPresentation.Components.DocumentHead;
const SportsMenu = MojitoModules.SportsMenu.module;
const useSanitizeHtml = MojitoPresentation.Hooks.useSanitizeHtml;

const log = Logger('CustomPageImpl');
const HEAD_DATA = {
    title: '$DBX.CUSTOM_PAGE.PAGE_TITLE',
    keywords: '$DBX.CUSTOM_PAGE.PAGE_KEYWORDS',
    description: '$DBX.CUSTOM_PAGE.PAGE_DESCRIPTION',
};

class CustomPageImpl extends UIViewImplementation {
    constructor(props) {
        const {mojitoTools} = props;
        const {l10n} = mojitoTools;

        super(props);

        // Creating the MetaFactory instance here to avoid re-creating it on every render()
        this.documentHeadMetaFactory = new BaseHeadMetaFactory(l10n);
    }

    componentDidMount() {
        loadScript(this.props.scriptUrl).then(script => {
            this.script = script;
        });
    }

    componentWillUnmount() {
        try {
            if (this.script) {
                // Unmount the script if needed
                document.head.removeChild(this.script);
                this.script = undefined;
            }
        } catch (e) {
            log.error(`Could not unmount script "${this.props.scriptUrl}". Do nothing`);
        }
    }

    render() {
        return (
            <>
                <DocumentHead data={HEAD_DATA} metaFactory={this.documentHeadMetaFactory} />
                <PageSplitLayout>
                    <FlexPane config={this.style.primaryContentContainer}>
                        <CustomHTML text={this.props.customHtml} />
                    </FlexPane>
                    <FlexPane config={this.style.secondaryContentContainer}>
                        <SportsMenu />
                        <GlobalLinks />
                    </FlexPane>
                </PageSplitLayout>
            </>
        );
    }
}

const CustomHTML = props => {
    const {text} = props;
    const innerHtml = {
        __html: useSanitizeHtml(text),
    };
    return <div dangerouslySetInnerHTML={innerHtml}></div>;
};

CustomHTML.propTypes = {
    text: PropTypes.string,
};

CustomPageImpl.getStyle = function (config) {
    return {
        primaryContentContainer: merge(
            {
                style: {
                    display: 'block',
                    flex: 'auto',
                },
            },
            config.primaryContentContainer || {}
        ),
        secondaryContentContainer: merge(
            {
                itemSpacing: '8px',
                style: {
                    flexDirection: 'column',
                },
            },
            config.secondaryContentContainer || {}
        ),
    };
};

export default UIPage('CustomPage', CustomPageImpl);
